import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { COUNTRIES, isQrIban, isValidIban, taxIdLabel, normalizeCountry, type IsoDate } from "@talentkontor/domain";
import { ErrorState, Loading } from "../../components/States";
import { Dialog } from "../../components/ui";
import { toast } from "../../components/Toast";
import { api } from "../../data";
import { useCreatorBase, useInstagramStatus, useProfileExtras, type CPayout, type CPreferences, type TalentSelf } from "./data";
import { instagramLinkLabel } from "./instagram-status";
import {
  creatorPayoutPatch, creatorRules, creatorTaxTexts, normalizeHandle, normalizePhone, orNull, payoutMethodsAvailable, profileFieldLabel, profileGaps, retroTaxConflicts, sortCountriesDach, taxIdsLabel, taxIdThresholdText, validateProfile, type ProfileForm as BaseForm,
} from "./model";
import { loadOtherProfiles, loadTaxHistory, saveProfileImage, savePayout, savePreferences, saveSignature, saveTalentLanguage, saveTalentProfile, saveTaxToProfiles, setTaxStatus } from "./actions";
import { DemoNote, SignaturePad, StoredImage, useCreatorAction, useCreatorContext } from "./ui";
import { DarstellungWahl } from "../../components/Darstellung";
import { usePlattformSchalter } from "../../components/Tarif";
import { dateOf, useT, type Lang } from "./sprache";
import { kleinunternehmerNurSitzland, steuerVerantwortung } from "../../model/steuer-verantwortung";
import { mailLanguageToSave } from "./mailsprache";
import { useUnsaved } from "./ungespeichert";
import { MeinePreisliste } from "./Preisliste";
import { MediaKitBereich } from "./MediaKit";
import { LOGO_MAX_CHARS } from "./rechnungPdf";
import { AdressVorschlag } from "../../components/AdressVorschlag";
import { moneyWhole } from "../../lib/format";
import { checkSwitchToSmallBusiness, revenueByYear, type RevenueRow } from "./geld";

/**
 * Dein Profil (12_app_creator/profil.html) mit dem Umfang des bisherigen Portals (TA-140 … TA-147, TA-106, TA-180):
 * persönliche Angaben, Rechnungs- und Steuerdaten, Bankverbindung bzw. Auszahlungsweg, Social-Handles,
 * WhatsApp-Einwilligung, gespeicherte Unterschrift, Profilbild, Sprache. Provision und Status verwaltet die Agentur.
 */

type Method = "sepa" | "swift" | "paypal" | "wise";
type Form = BaseForm & { payout_method: Method; account_number: string; routing_code: string; payout_email: string; company_name: string; legal_form: string; register_info: string };

/** Größe, die das Signaturfeld im Vertrag übernimmt (vertraege/daten.ts: PNG bis 140 KB) */
const SIGNATURE_MAX_BYTES = 140 * 1024;

function formOf(t: TalentSelf, payout: CPayout | null, prefs: CPreferences | null, orgCountry: string | null, uiLang: Lang): Form {
  return {
    first_name: t.first_name ?? "",
    last_name: t.last_name ?? "",
    display_name: t.display_name ?? "",
    nickname: t.nickname ?? "",
    email: t.email ?? "",
    street: t.street ?? "",
    postal_code: t.postal_code ?? "",
    city: t.city ?? "",
    country: t.country ?? orgCountry ?? "",
    small_business: t.small_business,
    vat_id: t.vat_id ?? "",
    tax_number: t.tax_number ?? "",
    company_name: t.company_name ?? "",
    legal_form: t.legal_form ?? "",
    register_info: t.register_info ?? "",
    instagram: t.instagram_handle ?? "",
    tiktok: t.tiktok_handle ?? "",
    youtube: t.youtube_handle ?? "",
    whatsapp_phone: t.whatsapp_phone ?? "",
    whatsapp_opt_in: !!t.whatsapp_opt_in_at,
    holder: payout?.bank_account_holder ?? "",
    iban: payout?.iban ?? "",
    bic: payout?.bic ?? "",
    bank_name: payout?.bank_name ?? "",
    bank_country: payout?.bank_country ?? t.country ?? "",
    // ohne eigene Wahl die Sprache, in der das Portal gerade erscheint (sonst zeigte das Feld „Deutsch“ bei englischem Portal)
    language: prefs?.language ?? uiLang,
    desktop_notifications: prefs?.desktop_notifications ?? true,
    payout_method: (payout?.payout_method ?? "sepa") as Method,
    account_number: payout?.account_number ?? "",
    routing_code: payout?.routing_code ?? "",
    payout_email: payout?.payout_email ?? "",
  };
}

/**
 * Unterschrift als PNG, höchstens 600 × 180 und 140 KB (Befund 25): so übernimmt der Vertrag sie beim Unterschreiben.
 * JPG und große Bilder werden im Browser umgerechnet und verkleinert.
 */
async function signatureAsPng(file: Blob): Promise<Blob> {
  if (file.type === "image/png" && file.size <= SIGNATURE_MAX_BYTES) return file;
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("Das Bild ließ sich nicht lesen."));
      i.src = url;
    });
    for (const width of [600, 480, 360, 240]) {
      const scale = Math.min(1, width / img.naturalWidth, (width * 0.3) / img.naturalHeight);
      const c = document.createElement("canvas");
      c.width = Math.max(1, Math.round(img.naturalWidth * scale));
      c.height = Math.max(1, Math.round(img.naturalHeight * scale));
      c.getContext("2d")?.drawImage(img, 0, 0, c.width, c.height);
      const png = await new Promise<Blob | null>((resolve) => c.toBlob(resolve, "image/png"));
      if (png && png.size <= SIGNATURE_MAX_BYTES) return png;
    }
    throw new Error("Die Unterschrift ist zu groß. Bitte zeichne sie hier oder wähle ein kleineres Bild.");
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Eigenes Logo für die im Portal erzeugte Rechnung (PARITAET-11): PNG, JPG oder SVG bis 2 MB, im Browser auf höchstens
 * 600 Pixel verkleinert und als JPEG mit weißem Hintergrund gespeichert (das PDF kennt nur JPEG).
 */
async function logoAsJpegDataUrl(file: Blob): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("Das Bild ließ sich nicht lesen."));
      i.src = url;
    });
    const w0 = img.naturalWidth || 600, h0 = img.naturalHeight || 200;
    for (const [max, quality] of [[600, 0.9], [480, 0.85], [360, 0.8], [240, 0.75]] as const) {
      const scale = Math.min(1, max / Math.max(w0, h0));
      const c = document.createElement("canvas");
      c.width = Math.max(1, Math.round(w0 * scale));
      c.height = Math.max(1, Math.round(h0 * scale));
      const ctx = c.getContext("2d");
      if (!ctx) break;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, c.width, c.height);
      ctx.drawImage(img, 0, 0, c.width, c.height);
      const data = c.toDataURL("image/jpeg", quality);
      if (data.startsWith("data:image/jpeg;base64,") && data.length <= LOGO_MAX_CHARS) return data;
    }
    throw new Error("Das Logo ist zu groß. Bitte ein kleineres Bild wählen.");
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function Profil() {
  const whatsappBereit = usePlattformSchalter("whatsapp_sender_ready").data === true;
  const { ws, scope, userId, lang, account, today } = useCreatorContext();
  const [taxFrom, setTaxFrom] = useState<string>("");
  const taxHistory = useQuery({ queryKey: ["tax_status_history", scope.talentId], queryFn: () => loadTaxHistory(api, scope.talentId) });
  // CREATOR-R3-7: Umsätze je Jahr (alle Agenturen) für die Voraussetzung des Wechsels zur Kleinunternehmerregelung
  const revenue = useQuery({ queryKey: ["creator-revenue-years", userId], enabled: !!userId, staleTime: 5 * 60_000, queryFn: () => api.read<RevenueRow[]>("my_revenue_by_year", {}).catch(() => [] as RevenueRow[]) });
  const t = useT();
  const base = useCreatorBase(scope);
  const extras = useProfileExtras(scope, userId);
  const igStatus = useInstagramStatus(scope, !!base.data?.talent.ig_enabled);
  const [edited, setForm] = useState<Form | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [signName, setSignName] = useState<string | null>(null);
  const [signImage, setSignImage] = useState<Blob | null>(null);
  const [drawKey, setDrawKey] = useState(0);
  const [ask, setAsk] = useState<null | "retro" | "clear" | "others">(null);
  const { run, busy } = useCreatorAction();
  const b = base.data;
  useUnsaved("profil", edited !== null);
  // Sprungziele aus „Erste Schritte“ und „Konto“ (#adresse, #steuerstatus, #auszahlung, #unterschrift, #sprache)
  const { hash } = useLocation();
  const loaded = !!b && !!extras.data;
  useEffect(() => {
    if (!loaded || !hash) return;
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (!el) return;
    el.scrollIntoView({ block: "center" });
    if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement) el.focus({ preventScroll: true });
  }, [loaded, hash]);

  // Weitere Profile derselben Person bei anderen (aktiven) Agenturen (Befund 13)
  const others = (account?.talentLinks ?? []).filter((l) => l.talent_id !== scope.talentId && account?.orgs[l.org_id]);
  const otherProfiles = useQuery({
    queryKey: ["creator", scope.talentId, "other-profiles", others.map((o) => o.talent_id).join(",")],
    enabled: others.length > 0,
    queryFn: () => loadOtherProfiles(api, others.map((o) => o.talent_id)),
  });

  // Eingaben starten mit den gespeicherten Werten; ab der ersten Änderung gilt der bearbeitete Stand
  const form = edited ?? (b && extras.data ? formOf(b.talent, extras.data.payout, extras.data.prefs, b.org.country, lang) : null);

  if (base.error || extras.error) return <ErrorState error={base.error ?? extras.error} retry={() => { void base.refetch(); void extras.refetch(); }} />;
  if (base.isPending || !b || !extras.data || !form || !userId) return <Loading />;

  const talent = b.talent;
  const payout = extras.data.payout;
  // Auszahlungswege (0042) auch vor dem ersten Speichern wählbar (0186); nur eine alte Zeile ohne die Spalte bleibt bei IBAN
  const methods = payoutMethodsAvailable(payout);
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...(f ?? form), [k]: v }));
  const check = validateProfile(form, { iban: payout?.iban, bic: payout?.bic }, lang);
  if (methods && (form.payout_method === "paypal" || form.payout_method === "wise") && form.payout_email.trim() && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.payout_email.trim())) {
    check.errors.push(t("Die Auszahlungs-Mail ist ungültig.", "The payout email is invalid."));
  }
  const gaps = profileGaps(talent, payout, creatorRules(b.defaults, b.org));
  const signature = extras.data.signature;
  const countries = sortCountriesDach(COUNTRIES, lang);
  const orgName = (orgId: string) => account?.orgs[orgId]?.name ?? t("Agentur", "Agency");
  const vatLabel = taxIdLabel(normalizeCountry(form.country || null), lang);
  // Aktiv geleert (Befund 9): gespeicherte Nummer, Feld jetzt leer
  // Mit Artikel im Satz („Du hast die Steuernummer geleert“; SICHT-CREATOR-15)
  const clearing = [
    ...(talent.vat_id && !form.vat_id.trim() ? [t(`die ${vatLabel}`, `the ${vatLabel}`)] : []),
    ...(talent.tax_number && !form.tax_number.trim() ? [t("die Steuernummer", "the tax number")] : []),
  ];
  const taxChanged = form.small_business !== talent.small_business || (orNull(form.vat_id) ?? null) !== (talent.vat_id ?? null) || (orNull(form.tax_number) ?? null) !== (talent.tax_number ?? null);

  // CREATOR-5: rückwirkender Wechsel; schon ausgestellte Rechnungen ab dem Stichtag, die nicht mehr passen
  const statusSwitch = form.small_business !== talent.small_business;
  const switchFrom = taxFrom || today;
  const retro = statusSwitch && switchFrom < today ? retroTaxConflicts(b.invoices, form.small_business, switchFrom) : { open: [], final: [] };
  const retroCount = retro.open.length + retro.final.length;
  // CREATOR-R3-7: Wechsel zur Kleinunternehmerregelung nur mit Umsatz unter der Vorjahresgrenze; Hinweis mit den Portalumsätzen
  const switchCheck = statusSwitch && form.small_business
    ? checkSwitchToSmallBusiness({ country: form.country || talent.country, from: switchFrom as IsoDate, currentYear: Number(today.slice(0, 4)), totals: revenueByYear(revenue.data ?? []), lang, money: (c, cy) => moneyWhole(c, cy) })
    : null;

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setShowErrors(true);
    if (check.errors.length) return;
    if (retroCount && ask !== "retro") setAsk("retro");
    else afterRetro();
  }
  function afterRetro() {
    if (clearing.length) setAsk("clear");
    else if (taxChanged && others.length) setAsk("others");
    else void save(false);
  }

  async function save(taxToOthers: boolean) {
    setAsk(null);
    const f = form!;
    const phone = normalizePhone(f.whatsapp_phone);
    // je Auszahlungsweg bereinigt: keine alte Mail oder IBAN nach einem Wechsel (0186)
    const payoutPatch: Record<string, unknown> = creatorPayoutPatch(f, methods);
    const payoutChanged = !!payout || Object.values(payoutPatch).some((v) => v !== null && v !== "sepa");
    // Steuerstatus mit Gültig-ab (0062): der Wechsel geht über den Verlauf, nicht still über den Schalter
    const statusChanged = f.small_business !== talent.small_business;
    const validFrom = taxFrom || today;
    const tax = { ...(statusChanged ? {} : { small_business: f.small_business }), vat_id: orNull(f.vat_id), tax_number: orNull(f.tax_number) };
    const ok = await run("profil", async () => {
      await saveTalentProfile(api, scope, {
        first_name: f.first_name.trim(), last_name: orNull(f.last_name), display_name: orNull(f.display_name), nickname: orNull(f.nickname), email: orNull(f.email),
        street: orNull(f.street), postal_code: orNull(f.postal_code), city: orNull(f.city), country: orNull(f.country), ...tax,
        company_name: orNull(f.company_name), legal_form: orNull(f.legal_form), register_info: orNull(f.register_info),
        instagram_handle: normalizeHandle(f.instagram), tiktok_handle: normalizeHandle(f.tiktok), youtube_handle: normalizeHandle(f.youtube),
        whatsapp_phone: phone, whatsapp_opt_in_at: f.whatsapp_opt_in && phone ? (talent.whatsapp_opt_in_at ?? new Date().toISOString()) : null,
      });
      if (payoutChanged) await savePayout(api, scope, payoutPatch as Omit<CPayout, "talent_id" | "org_id">, !!payout);
      const prefs = extras.data!.prefs;
      const languageChanged = !prefs || prefs.language !== f.language;
      if (languageChanged || prefs.desktop_notifications !== f.desktop_notifications) {
        await savePreferences(api, userId!, { language: f.language, desktop_notifications: f.desktop_notifications }, !!prefs);
      }
      // Neue Sprache gilt für die Person: auch die Mails aller Agenturen folgen ihr (talents.language). Nur beim Wechsel, sonst
      // überschriebe jedes Speichern still eine abweichende Wahl je Agentur aus den Benachrichtigungen (CREATOR-R2-5).
      const mailLanguage = mailLanguageToSave(prefs?.language, lang, f.language);
      if (mailLanguage) await saveTalentLanguage(api, [scope.talentId, ...others.map((o) => o.talent_id)], mailLanguage);
      if (statusChanged) await setTaxStatus(api, { orgId: scope.orgId, talentId: scope.talentId, smallBusiness: f.small_business, validFrom });
      if (taxToOthers) {
        await saveTaxToProfiles(api, others.map((o) => ({ talentId: o.talent_id, orgId: o.org_id })), tax);
        if (statusChanged) for (const o of others) await setTaxStatus(api, { orgId: o.org_id, talentId: o.talent_id, smallBusiness: f.small_business, validFrom });
      }
      setTaxFrom("");
      return true;
    }, taxToOthers ? t("Profil gespeichert, Steuerangaben auch bei deinen anderen Agenturen.", "Profile saved, tax details also at your other agencies.") : t("Profil gespeichert.", "Profile saved."));
    if (ok !== undefined) {
      // Formular aus der Datenbank neu füllen: was dort steht, sieht man auch
      setForm(null);
      setShowErrors(false);
    }
  }

  function takeFrom(talentId: string) {
    const p = otherProfiles.data?.find((x) => x.talent.id === talentId);
    if (!p) return;
    const s = (k: string, src: Record<string, unknown> | null) => (typeof src?.[k] === "string" ? (src[k] as string) : "");
    // Nur übernehmen, was dort steht: leere Felder der anderen Agentur überschreiben hier nichts
    setForm((f) => {
      const cur = f ?? form!;
      const take = (v: string, keep: string) => v.trim() || keep;
      return {
        ...cur,
        street: take(s("street", p.talent), cur.street), postal_code: take(s("postal_code", p.talent), cur.postal_code), city: take(s("city", p.talent), cur.city),
        country: take(s("country", p.talent), cur.country), small_business: p.talent.small_business === true,
        vat_id: take(s("vat_id", p.talent), cur.vat_id), tax_number: take(s("tax_number", p.talent), cur.tax_number),
        holder: take(s("bank_account_holder", p.payout), cur.holder), iban: take(s("iban", p.payout), cur.iban), bic: take(s("bic", p.payout), cur.bic),
        bank_name: take(s("bank_name", p.payout), cur.bank_name), bank_country: take(s("bank_country", p.payout), cur.bank_country),
        ...(methods ? {
          payout_method: (s("payout_method", p.payout) || cur.payout_method) as Method, account_number: take(s("account_number", p.payout), cur.account_number),
          routing_code: take(s("routing_code", p.payout), cur.routing_code), payout_email: take(s("payout_email", p.payout), cur.payout_email),
        } : {}),
      };
    });
    toast(t("Angaben übernommen. Prüfe sie und speichere dein Profil.", "Details copied. Check them and save your profile."));
  }

  async function saveSign() {
    const name = (signName ?? signature?.signing_name ?? talent.display_name ?? `${talent.first_name} ${talent.last_name ?? ""}`).trim();
    const ok = await run("unterschrift", async () => saveSignature(api, scope, name, signImage ? await signatureAsPng(signImage) : null, !!signature), t("Unterschrift gespeichert.", "Signature saved."));
    if (ok !== undefined) {
      setSignImage(null);
      setDrawKey((k) => k + 1);
    }
  }

  async function onSignFile(file: File | undefined) {
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type) || file.size > 5 * 1024 * 1024) {
      toast(t("Bitte ein PNG- oder JPG-Bild bis 5 MB wählen.", "Please choose a PNG or JPG image up to 5 MB."), "alert");
      return;
    }
    setSignImage(file);
  }

  async function onLogo(file: File | undefined) {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/svg+xml"].includes(file.type) || file.size > 2 * 1024 * 1024) {
      toast(t("Bitte ein PNG-, JPG- oder SVG-Bild bis 2 MB wählen.", "Please choose a PNG, JPG or SVG image up to 2 MB."), "alert");
      return;
    }
    await run("logo", async () => saveTalentProfile(api, scope, { invoice_logo: await logoAsJpegDataUrl(file) }), t("Logo gespeichert. Es steht ab jetzt auf deinen im Portal erzeugten Rechnungen.", "Logo saved. It now appears on invoices you create in the portal."));
  }

  async function onAvatar(file: File | undefined) {
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type) || file.size > 15_728_640) {
      toast(t("Bitte ein PNG- oder JPG-Bild bis 15 MB wählen.", "Please choose a PNG or JPG image up to 15 MB."), "alert");
      return;
    }
    await run("bild", () => saveProfileImage(api, scope, file), t("Profilbild gespeichert.", "Profile picture saved."));
  }

  const country = form.country || null;
  const taxTexts = creatorTaxTexts(country, lang);
  const smallBusinessLabel = country === "AT" ? t("Kleinunternehmer gemäß § 6 Abs. 1 Z 27 UStG", "Small business (§ 6 (1) no. 27 Austrian VAT Act)")
    : country === "CH" || country === "LI" ? t("Nicht mehrwertsteuerpflichtig", "Not registered for VAT")
    : t("Kleinunternehmer gemäß § 19 UStG", "Small business (§ 19 German VAT Act)");

  return (
    <main id="main" className="content tk-cr tk-cr-profil">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{t("Dein Creator-Portal", "Your creator portal")}</div>
          <h1>{t("Dein Profil", "Your profile")}</h1>
          <p>{others.length
            ? t(`Deine Angaben für ${ws.org.name}. Aus deinen anderen Agenturen kannst du sie übernehmen.`, `Your details for ${ws.org.name}. You can copy them from your other agencies.`)
            : t(`Deine Angaben für ${ws.org.name}.`, `Your details for ${ws.org.name}.`)}</p>
        </div>
      </div>

      {gaps.blocking.length > 0 && (
        <div className="notice" role="status">
          <strong>{t("Für Rechnungen fehlen noch:", "Still missing for invoices:")}</strong> {gaps.blocking.map((f) => profileFieldLabel(f, lang, talent.country)).join(", ")}. {t("Ohne diese Angaben kannst du keine Rechnung einreichen.", "Without these details you cannot submit an invoice.")}
        </div>
      )}

      {others.length > 0 && (
        <section className="panel stack">
          <h2>{t("Angaben aus einer anderen Agentur übernehmen", "Copy details from another agency")}</h2>
          <p>{t("Anschrift, Steuerangaben und Bankverbindung stehen bei jeder Agentur für sich. Übernimm sie hier, prüfe sie und speichere.", "Address, tax details and bank details are kept per agency. Copy them here, check them and save.")}</p>
          <div className="row">
            {others.map((o) => (
              <button key={o.talent_id} type="button" onClick={() => takeFrom(o.talent_id)} disabled={!otherProfiles.data}>{t(`Aus ${orgName(o.org_id)} übernehmen`, `Copy from ${orgName(o.org_id)}`)}</button>
            ))}
          </div>
        </section>
      )}

      <form className="panel stack" onSubmit={onSubmit} aria-busy={busy === "profil"} noValidate>
        <h2>{t("Persönliche Angaben", "Personal details")}</h2>
        <div className="grid">
          <label>{t("Vorname", "First name")}<input id="vorname" type="text" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} required autoComplete="given-name" /></label>
          <label>{t("Nachname", "Last name")}<input id="nachname" type="text" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} required={!form.company_name.trim()} autoComplete="family-name" /></label>
          <label>{t("Anzeigename im Portal", "Display name in the portal")}<input id="name" type="text" value={form.display_name} onChange={(e) => set("display_name", e.target.value)} placeholder={`${form.first_name} ${form.last_name}`.trim()} aria-describedby="hilfe-anzeigename" /><small id="hilfe-anzeigename">{t("So sieht dich deine Agentur in Listen. Auf Rechnungen und Verträgen stehen Vor- und Nachname bzw. deine Firma.", "How your agency sees you in lists. Invoices and contracts show your first and last name or your company.")}</small></label>
          <label>{t("Rufname", "Nickname")}<input id="rufname" type="text" maxLength={40} value={form.nickname} onChange={(e) => set("nickname", e.target.value)} placeholder={form.first_name} /></label>
          <label>
            {t("E-Mail", "Email")}
            <input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required autoComplete="email" />
            <small>{t("Hierhin schickt deine Agentur Hinweise. Deine Anmeldeadresse ändert sich dadurch nicht; die änderst du unter ", "Your agency sends notifications here. Your sign-in address does not change; you change it under ")}<Link to="/creator/konto">{t("Konto", "Account")}</Link>.</small>
          </label>
        </div>

        <h2>{t("Rechnungs- und Steuerdaten", "Invoice and tax details")}</h2>
        <div className="grid">
          <label>{t("Straße und Hausnummer", "Street and number")}<input id="adresse" type="text" value={form.street} onChange={(e) => set("street", e.target.value)} autoComplete="street-address" /></label>
          <label>{t("PLZ", "Postcode")}<input id="plz" type="text" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} autoComplete="postal-code" /></label>
          <label>{t("Ort", "City")}<input id="ort" type="text" value={form.city} onChange={(e) => set("city", e.target.value)} autoComplete="address-level2" /></label>
          <AdressVorschlag lang={lang} text={form.street} onPick={(a) => { set("street", a.street); if (a.postal_code) set("postal_code", a.postal_code); set("city", a.city); if (a.country) set("country", a.country); }} />
          <label>
            {t("Land", "Country")}
            <select id="land" value={form.country} onChange={(e) => set("country", e.target.value)}>
              <option value="">{t("Bitte wählen", "Please choose")}</option>
              {countries.map((c) => <option key={c.code} value={c.code}>{c.name[lang]}</option>)}
            </select>
          </label>
          <label>
            {t("Steuerstatus", "Tax status")}
            <select id="steuerstatus" value={form.small_business ? "ku" : "regel"} onChange={(e) => set("small_business", e.target.value === "ku")} aria-describedby="hilfe-steuer-verantwortung">
              <option value="regel">{taxTexts.regular}</option>
              <option value="ku">{smallBusinessLabel}</option>
            </select>
            {/* 0181 (Anwalt Punkt 11, 23): Steuerstatus ist Grundlage der Steuerhinweise auf deinen Rechnungen */}
            <small id="hilfe-steuer-verantwortung">{steuerVerantwortung(lang)}</small>
            {form.small_business && kleinunternehmerNurSitzland(country, lang) && <small id="hilfe-eu-kleinunternehmer">{kleinunternehmerNurSitzland(country, lang)}</small>}
            {(taxHistory.data?.length ?? 0) > 1 && (
              <small>{t("Verlauf: ", "History: ")}{taxHistory.data!.map((h) => `${h.valid_from <= "2000-01-01" ? t("zu Beginn", "initially") : `${t("ab", "from")} ${dateOf(h.valid_from, lang, ws.org.timezone)}`} ${h.small_business ? taxTexts.smallShort : taxTexts.regularShort}`).join(" · ")}</small>
            )}
          </label>
          {form.small_business !== talent.small_business && (
            <label>
              {t("Gilt ab", "Applies from")}
              <input type="date" value={taxFrom || today} min={`${Number(today.slice(0, 4)) - 1}-01-01`} onChange={(e) => setTaxFrom(e.target.value)} aria-describedby="hilfe-steuer-ab" />
              <small id="hilfe-steuer-ab">{form.small_business ? taxTexts.fromSmall : taxTexts.fromRegular}</small>
            </label>
          )}
          {switchCheck && (
            <div className={`notice tk-grid-full${switchCheck.level === "over" ? " tk-limit-over" : switchCheck.level === "near" ? " tk-limit-near" : ""}`} role={switchCheck.level === "ok" ? undefined : "alert"} id="hinweis-steuer-grenze">
              {switchCheck.text}
            </div>
          )}
          <label>
            {vatLabel}
            <input id="ustidnruid" type="text" value={form.vat_id} onChange={(e) => set("vat_id", e.target.value)} aria-describedby="hilfe-ustid" />
            <small id="hilfe-ustid">{taxTexts.vatIdHint}</small>
          </label>
          {country !== "CH" && country !== "LI" && (
            <label>
              {t("Steuernummer", "Tax number")}
              <input id="steuernummer" type="text" value={form.tax_number} onChange={(e) => set("tax_number", e.target.value)} aria-describedby="hilfe-steuernummer" />
              <small id="hilfe-steuernummer">{taxTexts.taxNumberHint}{t(" Ein geleertes Feld entfernt die gespeicherte Nummer nach Rückfrage.", " Clearing the field removes the saved number after a confirmation.")}</small>
            </label>
          )}
        </div>
        <fieldset className="stack">
          <legend>{t("Firma (nur wenn du über eine Firma abrechnest)", "Company (only if you invoice through a company)")}</legend>
          <div className="grid">
            <label>{t("Firmenname", "Company name")}<input id="firma" type="text" maxLength={160} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} autoComplete="organization" /></label>
            <label>{t("Rechtsform", "Legal form")}<input id="rechtsform" type="text" maxLength={60} value={form.legal_form} onChange={(e) => set("legal_form", e.target.value)} placeholder={t("zum Beispiel Einzelunternehmen, GbR, GmbH", "for example sole trader, partnership, GmbH")} /></label>
            <label>{t("Registerangaben", "Register details")}<input id="register" type="text" maxLength={160} value={form.register_info} onChange={(e) => set("register_info", e.target.value)} placeholder={registerExample(form.country, lang)} /></label>
          </div>
          <small>{companyNote(b.org.country, lang)}</small>
        </fieldset>

        <h2 id="auszahlung" tabIndex={-1} data-coco="profil-auszahlung">{t("Auszahlung", "Payout")}</h2>
        {methods && (
          <label>
            {t("Auszahlungsweg", "Payout method")}
            <select value={form.payout_method} onChange={(e) => set("payout_method", e.target.value as Method)}>
              <option value="sepa">{t("Überweisung (SEPA, IBAN)", "Bank transfer (SEPA, IBAN)")}</option>
              <option value="swift">{t("Auslandsüberweisung (SWIFT)", "International transfer (SWIFT)")}</option>
              <option value="paypal">PayPal</option>
              <option value="wise">Wise</option>
            </select>
          </label>
        )}
        {(!methods || form.payout_method === "sepa" || form.payout_method === "swift") ? (
          <div className="grid">
            <label>{t("Kontoinhaber", "Account holder")}<input id="kontoinhaber" type="text" value={form.holder} onChange={(e) => set("holder", e.target.value)} /></label>
            <label>
              IBAN
              <input id="iban" type="text" value={form.iban} onChange={(e) => set("iban", e.target.value)} autoComplete="off" aria-describedby="iban-status" />
              <small id="iban-status">{form.iban.trim() ? (!isValidIban(form.iban) ? t("Die Prüfziffer der IBAN stimmt nicht.", "The IBAN check digits do not match.")
                : isQrIban(form.iban) ? t("Das ist eine QR-IBAN; sie nimmt keine Überweisungen an. Bitte die normale IBAN eintragen.", "This is a QR-IBAN; it does not accept transfers. Please enter the normal IBAN.")
                : t("IBAN geprüft.", "IBAN checked.")) : " "}</small>
            </label>
            {methods && form.payout_method === "swift" && (
              <>
                <label>{t("Kontonummer (ohne IBAN)", "Account number (without IBAN)")}<input type="text" value={form.account_number} onChange={(e) => set("account_number", e.target.value)} autoComplete="off" /></label>
                <label>{t("Bankleitzahl / Routing", "Bank code / routing")}<input type="text" value={form.routing_code} onChange={(e) => set("routing_code", e.target.value)} autoComplete="off" /></label>
              </>
            )}
            <label>BIC<input id="bic" type="text" value={form.bic} onChange={(e) => set("bic", e.target.value)} autoComplete="off" /></label>
            <label>{t("Bank", "Bank")}<input id="bank" type="text" value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} /></label>
            <label>
              {t("Bankland", "Bank country")}
              <select id="bankland" value={form.bank_country} onChange={(e) => set("bank_country", e.target.value)}>
                <option value="">{t("Bitte wählen", "Please choose")}</option>
                {countries.map((c) => <option key={c.code} value={c.code}>{c.name[lang]}</option>)}
              </select>
            </label>
          </div>
        ) : (
          <label>
            {t("E-Mail-Adresse für die Auszahlung", "Email address for the payout")}
            <input type="email" value={form.payout_email} onChange={(e) => set("payout_email", e.target.value)} autoComplete="email" />
          </label>
        )}
        <small>{t("Änderst du die Bankverbindung, sieht deine Agentur das als Aufgabe und du bekommst eine Mail. So fällt eine fremde Änderung sofort auf.", "If you change your bank details, your agency sees this as a task and you receive an email. That way an unauthorised change is noticed immediately.")}</small>
        {api.mode === "demo" && <p>{t("Demo-IBAN nicht zahlbar.", "Demo IBAN, not payable.")}</p>}

        <h2>{t("Social Media", "Social media")}</h2>
        <div className="grid">
          <label>Instagram<input id="instagram" type="text" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} placeholder="@name" /></label>
          <label>TikTok<input id="tiktok" type="text" value={form.tiktok} onChange={(e) => set("tiktok", e.target.value)} placeholder="@name" /></label>
          <label>YouTube<input id="youtube" type="text" value={form.youtube} onChange={(e) => set("youtube", e.target.value)} placeholder={t("@kanal", "@channel")} /></label>
        </div>
        <small>{t("Deine Agentur nutzt die Namen für Leistungsbeschreibungen und Freigaben.", "Your agency uses the names for service descriptions and approvals.")}</small>

        <h2>{t("WhatsApp-Hinweise", "WhatsApp notifications")}</h2>
        {/* K-578: solange der Versand nicht eingerichtet ist (whatsapp_sender_ready), ehrlich „in Vorbereitung“ */}
        {!whatsappBereit && <p className="muted">{t("WhatsApp-Hinweise sind noch in Vorbereitung. Bis zum Start kommt nichts über WhatsApp, deine Einwilligung gilt dann ab dem ersten Hinweis.", "WhatsApp notifications are still being prepared. Nothing is sent via WhatsApp until the start; your consent then applies from the first notification.")}</p>}
        <label>
          {t("Handynummer", "Mobile number")}
          <input id="whatsapp" type="tel" value={form.whatsapp_phone} onChange={(e) => set("whatsapp_phone", e.target.value)} placeholder="+49 151 23456789" autoComplete="tel" />
        </label>
        <label className="row tk-check">
          <input type="checkbox" checked={form.whatsapp_opt_in} onChange={(e) => set("whatsapp_opt_in", e.target.checked)} />
          {t("Ich möchte Hinweise meiner Agentur (Rechnung, Vertrag, Nachricht) per WhatsApp bekommen.", "I would like to receive notifications from my agency (invoice, contract, message) via WhatsApp.")}
        </label>
        <small>
          {talent.whatsapp_opt_in_at ? t(`Eingewilligt am ${dateOf(talent.whatsapp_opt_in_at, lang, ws.org.timezone)}. `, `Consent given on ${dateOf(talent.whatsapp_opt_in_at, lang, ws.org.timezone)}. `) : ""}
          {t("Ob deine Agentur WhatsApp-Hinweise nutzt, legt sie selbst fest. Du kannst die Einwilligung jederzeit hier widerrufen oder per WhatsApp mit STOP. Antworten per WhatsApp liest niemand; schreib deiner Agentur bitte im Portal.", "Your agency decides whether it uses WhatsApp notifications. You can withdraw your consent here at any time or reply STOP via WhatsApp. Nobody reads replies via WhatsApp; please write to your agency in the portal.")}
        </small>

        <h2>{t("Sprache und Hinweise", "Language and notifications")}</h2>
        <div className="grid">
          <label>
            {t("Sprache", "Language")}
            <select id="sprache" value={form.language} onChange={(e) => set("language", e.target.value)}>
              <option value="de">Deutsch</option>
              <option value="en">English</option>
            </select>
            <small>{t("Gilt für dein Portal. Wechselst du sie hier, folgen auch die Mails aller deiner Agenturen; einzelne Agenturen stellst du danach unter Benachrichtigungen abweichend ein. Was gerade gilt, steht unter Konto.",
              "Applies to your portal. If you change it here, the emails from all your agencies follow; you can then set a different language for individual agencies under notifications. What currently applies is shown under account.")}</small>
          </label>
          <label className="row tk-check">
            <input type="checkbox" checked={form.desktop_notifications} onChange={(e) => set("desktop_notifications", e.target.checked)} />
            {t("Desktop-Benachrichtigungen bei neuen Nachrichten", "Desktop notifications for new messages")}
          </label>
        </div>
        {Object.keys(extras.data.prefs?.onboarding ?? {}).length > 0 && (
          <div className="row">
            <button type="button" onClick={() => void run("hinweise", () => savePreferences(api, userId, { onboarding: {} }, !!extras.data!.prefs), t("Ausgeblendete Hinweise erscheinen wieder.", "Hidden notes will show again."))} disabled={busy === "hinweise"}>
              {t("Ausgeblendete Hinweise wieder anzeigen", "Show hidden notes again")}
            </button>
          </div>
        )}

        {(showErrors || check.hints.length > 0) && (check.errors.length > 0 || check.hints.length > 0) && (
          <div className="stack tk-gap-sm" role="status">
            {showErrors && check.errors.length > 0 && <ul className="tk-findings tk-errors">{check.errors.map((m) => <li key={m}>{m}</li>)}</ul>}
            {check.hints.length > 0 && <ul className="tk-findings">{check.hints.map((m) => <li key={m}>{m}</li>)}</ul>}
          </div>
        )}
        {!form.small_business && !form.vat_id.trim() && !form.tax_number.trim() && (
          <small>{t(`${taxIdsLabel(country, lang)}: ohne eine der Angaben fragt deine Agentur ${taxIdThresholdText(country, lang)} nach.`, `${taxIdsLabel(country, lang)}: without one of them your agency will ask ${taxIdThresholdText(country, lang)}.`)}</small>
        )}
        <div className="row">
          <button className="primary" type="submit" disabled={busy === "profil"}>{busy === "profil" ? t("Wird gespeichert …", "Saving …") : t("Profil speichern", "Save profile")}</button>
          {edited && <button type="button" onClick={() => { setForm(null); setShowErrors(false); }}>{t("Änderungen verwerfen", "Discard changes")}</button>}
          {talent.ig_enabled && <Link className="btn " to="/creator/instagram">{instagramLinkLabel(igStatus.data, lang)}</Link>}
        </div>
        <small>{t("Provision und Status deines Profils verwaltet deine Agentur.", "Your agency manages the commission and status of your profile.")}</small>
        {talent.self_billing_agreed_on && (
          <small>
            {t(`Gutschriftverfahren vereinbart seit ${dateOf(talent.self_billing_agreed_on, lang, ws.org.timezone)}: Deine Agentur darf Rechnungen in deinem Namen als Gutschrift ausstellen. Jeder Gutschrift kannst du unter „Rechnungen“ widersprechen.`,
              `Self-billing agreed since ${dateOf(talent.self_billing_agreed_on, lang, ws.org.timezone)}: your agency may issue invoices in your name as self-billed invoices. You can object to each one under “Invoices”.`)}
          </small>
        )}
      </form>

      {/* Darstellung je Person (0120): Hell, Dunkel oder Automatisch, gilt in allen Agenturen */}
      {userId && <DarstellungWahl userId={userId} t={t} />}

      {/* 0072: Preisliste, wenn die Agentur sie freigibt */}
      <MeinePreisliste talentId={scope.talentId} lang={lang} timeZone={ws.org.timezone} />

      <section className="panel stack">
        <h2>{t("Profilbild", "Profile picture")}</h2>
        <div className="row">
          {talent.profile_image_path ? <StoredImage path={talent.profile_image_path} alt={t("Dein Profilbild", "Your profile picture")} className="tk-avatar-image" /> : <span className="account-avatar">{(form.first_name[0] ?? "").toUpperCase()}{(form.last_name[0] ?? "").toUpperCase()}</span>}
          <label>
            {t("Bild wählen", "Choose picture")}
            <input type="file" accept="image/png,image/jpeg" onChange={(e) => void onAvatar(e.target.files?.[0])} disabled={busy === "bild"} />
          </label>
          {talent.profile_image_path && <button onClick={() => void run("bild", () => saveProfileImage(api, scope, null), t("Profilbild entfernt.", "Profile picture removed."))} disabled={busy === "bild"}>{t("Entfernen", "Remove")}</button>}
        </div>
      </section>

      <section className="panel stack" aria-labelledby="rechnungslogo">
        <h2 id="rechnungslogo">{t("Logo für deine Rechnungen", "Logo for your invoices")}</h2>
        <p>{t("Steht oben auf der ersten Seite jeder Rechnung, die du im Portal aus deinen Angaben erzeugst. Eine eigene hochgeladene Rechnung bleibt, wie sie ist.",
          "Appears at the top of the first page of every invoice you create in the portal from your details. An invoice you upload yourself stays as it is.")}</p>
        <div className="row">
          {talent.invoice_logo && <img src={talent.invoice_logo} alt={t("Dein Rechnungslogo", "Your invoice logo")} className="tk-invoice-logo" />}
          <label>
            {talent.invoice_logo ? t("Anderes Logo wählen (PNG, JPG oder SVG, bis 2 MB)", "Choose another logo (PNG, JPG or SVG, up to 2 MB)") : t("Logo wählen (PNG, JPG oder SVG, bis 2 MB)", "Choose logo (PNG, JPG or SVG, up to 2 MB)")}
            <input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; void onLogo(f); }} disabled={busy === "logo"} />
          </label>
          {talent.invoice_logo && <button onClick={() => void run("logo", () => saveTalentProfile(api, scope, { invoice_logo: null }), t("Logo entfernt.", "Logo removed."))} disabled={busy === "logo"}>{t("Entfernen", "Remove")}</button>}
        </div>
      </section>

      <MediaKitBereich />

      <section className="panel stack">
        <h2 id="unterschrift" tabIndex={-1}>{t("Deine Unterschrift", "Your signature")}</h2>
        <p>{t("Gespeichert füllt sie beim Unterschreiben von Verträgen dein Signaturfeld vor. Nur du siehst sie. Bilder werden dafür als PNG gespeichert und bei Bedarf verkleinert.", "Once saved, it pre-fills your signature field when you sign contracts. Only you can see it. Images are stored as PNG and reduced in size if needed.")}</p>
        {signature && (
          <div className="row">
            <StoredImage path={signature.image_path} alt={t("Deine gespeicherte Unterschrift", "Your saved signature")} className="tk-signature-image" ink />
            <small>{t(`Gespeichert am ${dateOf(signature.updated_at, lang, ws.org.timezone)} als „${signature.signing_name}“`, `Saved on ${dateOf(signature.updated_at, lang, ws.org.timezone)} as “${signature.signing_name}”`)}</small>
          </div>
        )}
        {signature && !/\.png$/i.test(signature.image_path) && (
          <div className="notice">{t("Diese Unterschrift stammt aus einer älteren Fassung und ist kein PNG; Verträge übernehmen sie nicht. Bitte zeichne oder lade sie neu und speichere.", "This signature comes from an older version and is not a PNG; contracts do not use it. Please draw or upload it again and save.")}</div>
        )}
        <label>
          {t("Name unter der Unterschrift", "Name below the signature")}
          <input type="text" value={signName ?? signature?.signing_name ?? talent.display_name ?? `${talent.first_name} ${talent.last_name ?? ""}`.trim()} onChange={(e) => setSignName(e.target.value)} />
        </label>
        <SignaturePad key={drawKey} onChange={setSignImage} />
        <label>
          {t("Oder Bild hochladen (PNG oder JPG)", "Or upload an image (PNG or JPG)")}
          <input type="file" accept="image/png,image/jpeg" onChange={(e) => void onSignFile(e.target.files?.[0])} />
        </label>
        <div className="row">
          <button className="primary" onClick={() => void saveSign()} disabled={busy === "unterschrift" || (!signImage && !signature)}>
            {busy === "unterschrift" ? t("Wird gespeichert …", "Saving …") : t("Unterschrift speichern", "Save signature")}
          </button>
          {signImage && <small>{t("Neue Unterschrift bereit zum Speichern.", "New signature ready to save.")}</small>}
        </div>
      </section>

      <Dialog open={ask === "retro"} onClose={() => setAsk(null)} title={t("Schon ausgestellte Rechnungen passen nicht mehr", "Invoices already issued no longer fit")}>
        <p>{form.small_business
          ? t(`Ab ${dateOf(switchFrom, lang)} giltst du als Kleinunternehmer. Diese Rechnungen weisen danach trotzdem Umsatzsteuer aus; ausgewiesene Steuer schuldest du auch als Kleinunternehmer.`,
            `From ${dateOf(switchFrom, lang)} you are a small business. These invoices still show VAT; you owe VAT shown on an invoice even as a small business.`)
          : t(`Ab ${dateOf(switchFrom, lang)} weist du Umsatzsteuer aus. Diese Rechnungen sind danach ohne Umsatzsteuer gestellt.`,
            `From ${dateOf(switchFrom, lang)} you charge VAT. These invoices were issued without VAT.`)}</p>
        {retro.open.length > 0 && (
          <>
            <p><strong>{t("Noch offen: korrigieren oder zurückziehen", "Still open: correct or withdraw")}</strong></p>
            <ul>
              {retro.open.map((i) => (
                <li key={i.id}><Link to={i.status === "submitted" ? `/creator/rechnungen?rechnung=${i.id}` : `/creator/rechnungen/einreichen?rechnung=${i.id}`}>{i.number ?? t("ohne Nummer", "no number")}</Link>{i.issue_date ? ` · ${dateOf(i.issue_date, lang)}` : ""}</li>
              ))}
            </ul>
          </>
        )}
        {retro.final.length > 0 && (
          <>
            <p><strong>{t("Schon freigegeben oder bezahlt: Korrekturrechnung nötig", "Already approved or paid: corrective invoice needed")}</strong></p>
            <ul>{retro.final.map((i) => <li key={i.id}>{i.number ?? t("ohne Nummer", "no number")}{i.issue_date ? ` · ${dateOf(i.issue_date, lang)}` : ""}</li>)}</ul>
            <p>{t("Sprich mit deiner Steuerberatung. Deine Agentur bekommt beim Speichern einen Hinweis mit diesen Nummern.", "Talk to your tax adviser. Your agency gets a notice with these numbers when you save.")}</p>
          </>
        )}
        <div className="row">
          <button className="primary" onClick={() => afterRetro()}>{t("Trotzdem speichern", "Save anyway")}</button>
          <button onClick={() => setAsk(null)}>{t("Abbrechen", "Cancel")}</button>
        </div>
      </Dialog>
      <Dialog open={ask === "clear"} onClose={() => setAsk(null)} title={t("Gespeicherte Nummer entfernen?", "Remove saved number?")}>
        <p>{t(`Du hast ${clearing.join(" und ")} geleert. Beim Speichern wird die gespeicherte Angabe entfernt; sie steht dann nicht mehr auf neuen Rechnungen.`, `You cleared ${clearing.join(" and ")}. Saving removes the saved value; it will no longer appear on new invoices.`)}</p>
        <div className="row">
          <button className="danger" onClick={() => (taxChanged && others.length ? setAsk("others") : void save(false))}>{t("Entfernen und speichern", "Remove and save")}</button>
          <button onClick={() => setAsk(null)}>{t("Abbrechen", "Cancel")}</button>
        </div>
      </Dialog>
      <Dialog open={ask === "others"} onClose={() => setAsk(null)} title={t("Steuerangaben auch bei deinen anderen Agenturen?", "Tax details for your other agencies too?")}>
        <p>{t(`Steuerstatus, ${vatLabel} und Steuernummer gelten für dich als Person. Sollen sie auch bei ${others.map((o) => orgName(o.org_id)).join(", ")} so stehen? Sonst rechnet eine Agentur womöglich mit, die andere ohne Umsatzsteuer.`,
          `Tax status, ${vatLabel} and tax number apply to you as a person. Should they also be set like this at ${others.map((o) => orgName(o.org_id)).join(", ")}? Otherwise one agency may calculate with VAT and the other without.`)}</p>
        <div className="row">
          <button className="primary" onClick={() => void save(true)}>{t("Überall übernehmen", "Apply everywhere")}</button>
          <button onClick={() => void save(false)}>{t("Nur hier speichern", "Save here only")}</button>
        </div>
      </Dialog>
      <DemoNote />
    </main>
  );
}

/** SAAS-9: Beispiel für die Registerangaben nach dem Land des Creators (Firmensitz) */
export function registerExample(country: string | null | undefined, lang: "de" | "en"): string {
  const c = normalizeCountry(country ?? null);
  if (c === "AT") return lang === "en" ? "for example FN 123456a, commercial court Vienna" : "zum Beispiel FN 123456a, Handelsgericht Wien";
  if (c === "CH" || c === "LI") return lang === "en" ? "for example commercial register of the canton of Zurich, CHE-123.456.789" : "zum Beispiel Handelsregister des Kantons Zürich, CHE-123.456.789";
  return lang === "en" ? "for example local court, HRB 12345" : "zum Beispiel Amtsgericht Musterstadt HRB 12345";
}

/**
 * SAAS-9: Hinweis zur Firma. Die Künstlersozialabgabe ist deutsches Recht; der Satz dazu steht nur, wenn die gewählte Agentur
 * in Deutschland sitzt.
 */
export function companyNote(agencyCountry: string | null | undefined, lang: "de" | "en"): string {
  const base = lang === "en" ? "Then shown as the issuer on your invoices and in contracts." : "Steht dann als Aussteller auf deinen Rechnungen und in Verträgen.";
  if (normalizeCountry(agencyCountry ?? null) !== "DE") return base;
  return lang === "en" ? `${base} For a GmbH, UG or AG your agency pays no artists' social security levy.` : `${base} Bei GmbH, UG oder AG fällt für deine Agentur keine Künstlersozialabgabe an.`;
}
